import { Accordion, AccordionBody, AccordionHeader, AccordionItem } from 'reactstrap';
import './process-steps-viewer.css';
import { ProcessStepItemCard } from './process-step-item-card/process-step-item-card';
import { useRef } from 'react';
import { ModelNameHandler } from '../model-name-handler/model-name-handler';
import { Project } from '../../../types/api/project/get-project-success-response';
import { Tool } from '../../../types/api/tool/get-tools-success-response';
import { getImageUrl } from '../../../../../utils/image-helper/get-image-url';
import { NewProcessStepWithEndpointModelGeneratedAndDoneAndStartingModelName, ProcessStepWithEndpointModelGeneratedAndDoneAndStartingModelName } from '../../../types/api/process-step/get-process-steps-success-response';
import { CMSButton } from '../../../../../components/cms/button/cms-button.';

type ProcessStepsViewerProps = {
    project: Project;
    processSteps: (ProcessStepWithEndpointModelGeneratedAndDoneAndStartingModelName | NewProcessStepWithEndpointModelGeneratedAndDoneAndStartingModelName)[];
    selectedModelUUID: string | null;
    selectedProcessStepId: number | null;
    selectedProcessStepItemId: number | null;
    onProcessStepItemModelSelect: (uuid: string | null) => void;
    onProcessStepItemSelect: (processStepItemId: number | null) => void;
    onProcessStepSelect: (processStepId: number | null) => void;
    onAddNewProcessStep: (id: number, name: string) => void;
    onAddNewProcessStepItem: (id: number, processStepId: number) => void;
    onProcessStepNameChange?: (processStepId: number, name: string) => void;
    onProcessStepDelete?: (processStepId: number) => void;
    onProcessStepItemDelete?: (processStepItemId: number) => void;
    disabled: boolean;
    onStartingModelClick?: (processStepItemId: number) => void;
    onSettingStartingModelVisible?: (processStepItemId: number, visible: boolean) => void;
    onSettingModelToBeMovedConnectedToReferenceModel?: (processStepItemId: number, connected: boolean) => void;
    onReferenceModelClick?: (processStepItemId: number) => void;
    onOtherModelChooseForReferenceModelClick?: (processStepItemId: number) => void;
    onToolChooseForProcessStepItem: (processStepItemId: number, tool: Tool) => void;
    onToolDeleteForProcessStepItem?: (processStepItemId: number) => void;
    onEndpointModelClick?: (processStepItemId: number) => void;
    onEndpointModelDeleteClick?: (processStepItemId: number) => void;
    onDoneClick?: (processStepItemId: number) => void;
};

export const ProcessStepsViewer = ({
    processSteps,
    selectedModelUUID,
    selectedProcessStepItemId,
    selectedProcessStepId,
    onProcessStepItemModelSelect,
    onProcessStepSelect,
    onProcessStepItemSelect,
    onAddNewProcessStep,
    onAddNewProcessStepItem,
    project,
    onToolChooseForProcessStepItem,
    onProcessStepNameChange,
    onProcessStepDelete,
    onProcessStepItemDelete,
    disabled,
    onStartingModelClick,
    onSettingStartingModelVisible,
    onSettingModelToBeMovedConnectedToReferenceModel,
    onReferenceModelClick,
    onToolDeleteForProcessStepItem,
    onEndpointModelClick,
    onEndpointModelDeleteClick,
    onDoneClick
}: ProcessStepsViewerProps) => {
    const newProcessStepIdRef = useRef(-1);
    const newProcessStepItemIdRef = useRef(-1);

    const handleAddNewProcessStepClick = () => {
        onAddNewProcessStep(newProcessStepIdRef.current, `új lépés ${ newProcessStepIdRef.current * -1 }`);
        newProcessStepIdRef.current--;
    };

    const handleAddNewProcessStepItemClick = (processStepId: number) => {
        onAddNewProcessStepItem(newProcessStepItemIdRef.current--, processStepId);
    };

    const handleToolChoose = (processStepItemId: number, tool: Tool) => {
        onToolChooseForProcessStepItem(processStepItemId, tool);
    };

    const handleProcessStepDelete = (processStepId: number) => {
        if (onProcessStepDelete) onProcessStepDelete(processStepId);
    };

    return (
        <div className='process-steps-viewer'>

            {
                disabled && (
                    <div className="loader">
                        <div className='spinner'>
                            <div className='spinner-border'></div>
                        </div>
                    </div>
                )
            }

            <Accordion
                className='process-steps-accordion'
                open={selectedProcessStepId ? selectedProcessStepId.toString() : ''}
                toggle={
                    (id) => {
                        if (id !== selectedProcessStepId?.toString()) onProcessStepSelect(parseInt(id, 10));
                        else onProcessStepSelect(null);
                    }
                }
            >
                {
                    processSteps.map(processStep => (
                        <AccordionItem key={processStep.id} className='mb-2'>
                            <AccordionHeader
                                targetId={processStep.id!.toString()}
                                className={`root-accordiom-header ${ project.workInstructionModule && !processStep.workInstruction ? 'extra-step' : '' }`}
                            >
                                <ModelNameHandler
                                    editIconColor='black'
                                    defaultValue={processStep.name ?? ''}
                                    onSave={
                                        (name) => {
                                            if (onProcessStepNameChange) onProcessStepNameChange(processStep.id!, name);
                                        }
                                    }
                                />
                                {
                                    (processStep.order || processStep.id! < 0) && (
                                        <img
                                            src={getImageUrl('delete-icon.png')}
                                            alt="del"
                                            onClick={
                                                (e) => {
                                                    e.stopPropagation();
                                                    handleProcessStepDelete(processStep.id!);
                                                }
                                            }
                                        />
                                    )
                                }
                            </AccordionHeader>
                            <AccordionBody accordionId={processStep.id!.toString()} className='root-accordiom-body'>
                                {
                                    processStep.processStepItems?.map((processStepItem, index) => (
                                            <ProcessStepItemCard
                                                project={project}
                                                key={processStepItem.id}
                                                selectedModelUUID={selectedModelUUID}
                                                processStepItem={processStepItem}
                                                index={index}
                                                isOpen={processStepItem.id === selectedProcessStepItemId}
                                                onOpenAccordion={() => onProcessStepItemSelect(processStepItem.id)}
                                                onCloseAccordion={() => onProcessStepItemSelect(null)}
                                                onToolChoose={(tool) => handleToolChoose(processStepItem.id, tool)}
                                                onProcessStepItemDelete={
                                                    () => {
                                                        if (onProcessStepItemDelete) onProcessStepItemDelete(processStepItem.id);
                                                    }
                                                }
                                                onStartingModelClick={
                                                    () => {
                                                        if (onStartingModelClick) onStartingModelClick(processStepItem.id);
                                                    }
                                                }
                                                onSettingStartingModelVisible={
                                                    (visible) => {
                                                        if (onSettingStartingModelVisible) {
                                                            onSettingStartingModelVisible(processStepItem.id, visible);
                                                        }
                                                    }
                                                }
                                                onSettingModelToBeMovedConnectedToReferenceModel={
                                                    (connected) => {
                                                        if (onSettingModelToBeMovedConnectedToReferenceModel) {
                                                            onSettingModelToBeMovedConnectedToReferenceModel(processStepItem.id, connected);
                                                        }
                                                    }
                                                }
                                                onReferenceModelClick={
                                                    () => {
                                                        if (onReferenceModelClick) onReferenceModelClick(processStepItem.id);
                                                    }
                                                }
                                                onToolDeleteClick={() => {
                                                    if (onToolDeleteForProcessStepItem) onToolDeleteForProcessStepItem(processStepItem.id);
                                                }}
                                                onEndpointModelClick={
                                                    () => {
                                                        if (onEndpointModelClick) onEndpointModelClick(processStepItem.id);
                                                    }
                                                }
                                                onEndpointModelDeleteClick={
                                                    () => {
                                                        if (onEndpointModelDeleteClick) onEndpointModelDeleteClick(processStepItem.id);
                                                    }
                                                }
                                                onDoneClick={
                                                    () => {
                                                        if (onDoneClick) onDoneClick(processStepItem.id);
                                                    }
                                                }
                                            />
                                        )
                                    )
                                }
                                <div className='text-center my-1'>
                                    <CMSButton text='+ Elem hozzáadása' type='blue' onClick={() => handleAddNewProcessStepItemClick(processStep.id!)} />
                                </div>
                            </AccordionBody>
                        </AccordionItem>
                    ))
                }
            </Accordion>
        
            <CMSButton
                text='+ Lépés hozzáadása'
                type='pink'
                style={{ marginTop: '10px', width: '100%' }}
                onClick={handleAddNewProcessStepClick}
            />
        </div>
    );
};